import {
    Button,
    Checkbox,
    Dropdown,
    Flyout,
    FormField,
    Menu,
    ProgressSpinner,
    SplitButton,
    Switch,
    T,
} from "@bbnpm/bb-ui-framework";
import InfoCircle from "@bloomberg/web-icons-react/InfoCircle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hasMigrated, isManagedMac, MigrationPhases, Settings } from "../../settings.js";
import { createLaunchAPI } from "./launch-api.js";
import { Constants } from "./launch-constants.js";
import { getDevProgressState } from "./launch-dev-states.js";
import { Helpers } from "./launch-helpers.js";
import "./launch.min.css";

const UpgradeClientModal = ({ isOpen, onClose, installedVersion, latestVersion }) => {
    if (!isOpen) return null;

    return (
        <div className="support-modal-overlay" onClick={onClose}>
            <div
                className="support-modal-content support-modal-narrow"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="support-modal-header">
                    <T.H2>Update Your Parallels Client</T.H2>
                    <button
                        className="support-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>
                <div className="support-modal-body">
                    {isManagedMac() ? (
                        <T.Body>
                            You last connected using an older Parallels client. Please see the BBA
                            Parallels RAS TEAM page for how to update your Parallels client and get
                            the newest features and improvements.
                        </T.Body>
                    ) : (
                        <T.Body>
                            You last connected using an older Parallels client. If you haven&apos;t
                            updated already, download the latest version for the newest features and
                            improvements.
                        </T.Body>
                    )}
                    {(installedVersion || latestVersion) && (
                        <dl className="support-modal-versions">
                            {/* `as` keeps the dt/dd tags, which the <dl> needs semantically and
                                which launch.scss targets for the two-column grid. */}
                            {installedVersion && (
                                <>
                                    <T.BodyLarge as="dt">Your version</T.BodyLarge>
                                    <T.BodyLarge as="dd">{installedVersion}</T.BodyLarge>
                                </>
                            )}
                            {latestVersion && (
                                <>
                                    <T.BodyLarge as="dt">Latest version</T.BodyLarge>
                                    <T.BodyLarge as="dd">{latestVersion}</T.BodyLarge>
                                </>
                            )}
                        </dl>
                    )}
                    <div className="support-modal-downloads">
                        {isManagedMac() ? (
                            <Button
                                as="a"
                                kind="primary"
                                href={Constants.MigrationFaqUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Go to TEAM &lt;GO&gt;
                            </Button>
                        ) : (
                            <Button
                                as="a"
                                kind="primary"
                                href={Helpers.getBasePath() + "downloads"}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Go to Downloads
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Pre-migration opt-in to the Parallels client.
const ParallelsPreview = ({ isOn, onToggle }) => (
    <div className="parallels-preview">
        <div className="parallels-preview-switch">
            <Switch
                checked={isOn}
                onChange={(event, checked) => onToggle(checked)}
                label="Preview the Parallels Client"
                labelPosition="right"
            />
            <Flyout
                content={
                    <div className="parallels-preview-tooltip">
                        Bloomberg Anywhere is moving to the Parallels Client. Turn this on to
                        install it and get ready for the switch.
                    </div>
                }
            >
                <InfoCircle className="info-icon" />
            </Flyout>
        </div>

        {isOn && (
            <div className="parallels-preview-panel">
                <T.Body>
                    Install Parallels Client to prepare for the switch. You can turn this preview
                    off anytime to return to Citrix.
                </T.Body>
                <div className="parallels-preview-install">
                    {isManagedMac() ? (
                        <div className="parallels-preview-install-text">
                            <T.Body as="p">
                                Bloomberg managed Mac devices already have the Parallels client
                                installed
                            </T.Body>
                            <T.BodySmall
                                as="a"
                                href={Constants.MigrationFaqUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Need help &gt;
                            </T.BodySmall>
                        </div>
                    ) : (
                        <div className="parallels-preview-install-text">
                            <T.H5>Install Parallels Client</T.H5>
                            <T.BodySmall
                                as="a"
                                href={Helpers.getBasePath() + "downloads"}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Get install help? &gt;
                            </T.BodySmall>
                        </div>
                    )}
                    {!isManagedMac() && (
                        <div className="parallels-preview-install-actions">
                            {/* as="a": a <button> inside an <a> does not reliably follow the link. */}
                            <Button
                                as="a"
                                kind="secondary"
                                href={Helpers.getBasePath() + "downloads/parallels/windows"}
                            >
                                Windows
                            </Button>
                            <Button
                                as="a"
                                kind="secondary"
                                href={Helpers.getBasePath() + "downloads/parallels/mac"}
                            >
                                Mac
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
);

// Server default outside opt-in, or when the wanted protocol is unavailable.
const protocolForPreview = (previewOn) => {
    if (Settings.migration.phase !== MigrationPhases.OptIn) {
        return Settings.ui.protocol;
    }

    const wanted = previewOn
        ? Constants.Protocols.ParallelsClient
        : Constants.Protocols.CitrixClient;

    return Settings.ui.availableProtocols.includes(wanted) ? wanted : Settings.ui.protocol;
};

const MIGRATION_REQUIRED_MESSAGE = "Parallels Client is now required to launch Bloomberg Anywhere";

const MigrationBanner = () => (
    <div className="migration-banner">
        <InfoCircle className="migration-banner-icon" />
        <div className="migration-banner-body">
            <T.BodyBold as="p">{MIGRATION_REQUIRED_MESSAGE}</T.BodyBold>
            <div className="migration-banner-links">
                {isManagedMac() ? (
                    <T.BodyBold
                        as="a"
                        href={Constants.MigrationFaqUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Need help &gt;
                    </T.BodyBold>
                ) : (
                    <>
                        <T.BodyBold
                            as="a"
                            href={Helpers.getBasePath() + "downloads"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Install Parallels Client &gt;
                        </T.BodyBold>
                        <T.BodyBold
                            as="a"
                            href={Helpers.getBasePath() + "downloads"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Get install help &gt;
                        </T.BodyBold>
                    </>
                )}
            </div>
        </div>
    </div>
);

const MigrationBar = () => (
    <div className="migration-bar">
        <div className="migration-bar-inner">
            <div className="migration-bar-message">
                <T.H3>{MIGRATION_REQUIRED_MESSAGE}</T.H3>
                <div className="migration-bar-detail">
                    {/* as="span": these two sit on one line, so neither can be a <p>. */}
                    <T.Body as="span">
                        If you are having issues, Citrix remains available anytime.
                    </T.Body>
                    <T.BodyBold
                        as="a"
                        href={Helpers.getBasePath() + "downloads"}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn about Parallels &gt;
                    </T.BodyBold>
                </div>
            </div>

            <div className="migration-bar-install">
                {isManagedMac() ? (
                    <div className="migration-bar-install-text migration-bar-install-managed">
                        <T.Body as="p">
                            Bloomberg managed Mac devices already have the Parallels client
                            installed
                        </T.Body>
                        <T.BodyBold
                            as="a"
                            href={Constants.MigrationFaqUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Need help &gt;
                        </T.BodyBold>
                    </div>
                ) : (
                    <div className="migration-bar-install-text">
                        <T.H4>Install Parallels Client</T.H4>
                        <T.BodyBold
                            as="a"
                            href={Helpers.getBasePath() + "downloads"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Get install help &gt;
                        </T.BodyBold>
                    </div>
                )}

                {!isManagedMac() && (
                    <div className="migration-bar-install-actions">
                        <Button
                            as="a"
                            kind="secondary"
                            href={Helpers.getBasePath() + "downloads/parallels/windows"}
                        >
                            Windows
                        </Button>
                        <Button
                            as="a"
                            kind="secondary"
                            href={Helpers.getBasePath() + "downloads/parallels/mac"}
                        >
                            Mac
                        </Button>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const Launch = ({ setHasServerError }) => {
    const devProgress = getDevProgressState();

    // Progress
    const [progressText, setProgressText] = useState(
        devProgress?.text ?? Constants.DefaultProgressText,
    );
    const [progressEnabled, setProgressEnabled] = useState(Boolean(devProgress));

    // Outdated Parallels client nudge (non-blocking)
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

    // Parallels preview switch
    const [parallelsPreviewOn, setParallelsPreviewOn] = useState(
        Settings.migration.parallelsPreview,
    );

    // Which protocol the Launch button fires and the dropdown marks as recommended.
    const activeProtocol = protocolForPreview(parallelsPreviewOn);

    // Track current launch protocol
    const [currentLaunchProtocol, setCurrentLaunchProtocol] = useState(
        devProgress?.protocol ?? null,
    );

    // Application dropdown
    const [application, setApplication] = useState(() => {
        return Settings.ui.defaultApplication;
    });

    // Region drop down
    const [currentRegion, setCurrentRegion] = useState(() => {
        return Settings.ui.currentRegion;
    });

    // Excel checkbox
    const [excelChecked, setExcelChecked] = useState(() => {
        return Settings.ui.useExcel;
    });

    const [applicationDisplay, setApplicationDisplay] = useState(() => {
        return Settings.ui.defaultApplication;
    });

    // Tooltips
    const [excelTooltip, setExcelTooltip] = useState({});

    // Wire up callbacks to be used by functions
    const showProgressCallback = useCallback(() => {
        setProgressText(Constants.DefaultProgressText);
        setProgressEnabled(true);
    }, []);
    const hideProgressCallback = useCallback(() => setProgressEnabled(false), []);
    const setProgressTextCallback = useCallback((text) => setProgressText(text), []);
    const clearErrorsCallback = useCallback(() => {
        clearErrors();
        setHasServerError(false);
    }, [setHasServerError]);

    const api = useMemo(
        () =>
            createLaunchAPI({
                showProgress: showProgressCallback,
                hideProgress: hideProgressCallback,
                setProgressText: setProgressTextCallback,
                clearErrors: clearErrorsCallback,
                currentRegion,
                excelChecked,
                application,
                applicationDisplay,
            }),
        [
            showProgressCallback,
            hideProgressCallback,
            setProgressTextCallback,
            clearErrorsCallback,
            currentRegion,
            excelChecked,
            application,
            applicationDisplay,
        ],
    );

    // "Click launch" on enter key
    // TODO: Browser may block fetch if hitting return too
    // soon after other UI updates (e.g. changing dropdown)
    const launchButtonRef = useRef();
    useEffect(() => {
        window.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                launchButtonRef.current.click();
            }
        });
    }, []);

    // Handle auto launch (if requested/enabled)
    useEffect(() => {
        api.handleAutoLaunch();
    }, [api]);

    // Alert on an outdated client. Skipped for users who have not started
    // migrating, who have no Parallels client to upgrade, and during auto-launch.
    useEffect(() => {
        if (
            Settings.clientVersion.isOutdated &&
            Settings.migration.phase !== MigrationPhases.None &&
            !Settings.autoLaunch.enabled
        ) {
            setIsUpgradeModalOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeUpgradeModal = () => setIsUpgradeModalOpen(false);

    // Session Expiration
    useEffect(() => {
        api.logoutAfterSessionTimeout();
    }, [api]);

    function addLaunchButtonDropdownItemIfAvailable(items, protocol, displayName) {
        const available = Settings.ui.availableProtocols;
        const bothBrowsersAvailable =
            available.includes(Constants.Protocols.ParallelsBrowser) &&
            available.includes(Constants.Protocols.CitrixBrowser);

        // Once migration starts, Citrix is never labelled Recommended, even when Launch fires it.
        const legacyCitrix =
            protocol === Constants.Protocols.CitrixClient &&
            Settings.migration.phase !== MigrationPhases.None;
        // In opt-in, the Citrix entry Launch fires gets no suffix at all.
        const plainOptInCitrix =
            legacyCitrix &&
            activeProtocol === protocol &&
            Settings.migration.phase === MigrationPhases.OptIn;

        let label = `Launch with ${displayName}`;
        let recommended = activeProtocol === protocol && !legacyCitrix;
        let recommendedBrowser = Constants.DefaultLaunchProtocolBrowser === protocol;

        if (recommended) {
            label += " (Recommended)";
        } else if (recommendedBrowser) {
            label = "Launch with Browser";
        } else if (protocol === Constants.Protocols.CitrixBrowser && bothBrowsersAvailable) {
            label = "Launch with Browser (Legacy)";
        } else if (legacyCitrix && !plainOptInCitrix) {
            label += " (Legacy)";
        }

        if (available.includes(protocol)) {
            items.push({
                label: label,
                protocol: protocol,
                onSelect: () => handleLaunchWithTracking(protocol),
            });
        }
    }

    function clearErrors() {
        const banner = document.querySelectorAll("[class*='bbui-banner error']")[0];
        if (banner) {
            banner.remove();
        }
    }

    // Wrapper to track launch protocol
    function handleLaunchWithTracking(protocol) {
        setCurrentLaunchProtocol(protocol);
        api.handleLaunch(protocol);
    }

    // Handle browser launch from progress overlay
    function handleBrowserLaunchFromProgress() {
        setCurrentLaunchProtocol(Constants.Protocols.ParallelsBrowser);
        api.launchStoredResponseInBrowser();
    }

    const buildLaunchButtonDropdownMenu = () => {
        const items = [];

        // Parallels
        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.ParallelsClient,
            Constants.ProtocolLabels.ParallelsClient,
        );

        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.ParallelsBrowser,
            Constants.ProtocolLabels.ParallelsBrowser,
        );

        // DCV
        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.DcvClientFile,
            Constants.ProtocolLabels.DcvClientFile,
        );

        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.DcvClientHandler,
            Constants.ProtocolLabels.DcvClientHandler,
        );

        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.DcvBrowser,
            Constants.ProtocolLabels.DcvBrowser,
        );

        // Citrix
        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.CitrixClient,
            Constants.ProtocolLabels.CitrixClient,
        );

        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.CitrixBrowser,
            Constants.ProtocolLabels.CitrixBrowser,
        );

        // RDP
        addLaunchButtonDropdownItemIfAvailable(
            items,
            Constants.Protocols.RDP,
            Constants.ProtocolLabels.RDP,
        );

        // Recommended entry first; Menu takes only label/onSelect.
        const active = items.filter((item) => item.protocol === activeProtocol);
        const rest = items.filter((item) => item.protocol !== activeProtocol);

        return [...active, ...rest].map(({ label, onSelect }) => ({ label, onSelect }));
    };

    return (
        <div className="launch">
            <div className="content">
                {Settings.migration.phase === MigrationPhases.OptIn && (
                    <ParallelsPreview isOn={parallelsPreviewOn} onToggle={setParallelsPreviewOn} />
                )}
                {/* Migrated only, not hasMigrated(): the settled phase is the one that
                    retires this announcement. */}
                {Settings.migration.phase === MigrationPhases.Migrated && <MigrationBanner />}

                <div className="top">
                    <T.H2>Launch</T.H2>
                    {/* as="p": the `.top p` rule (uppercase, muted, margin) targets the tag. */}
                    <T.Label as="p">
                        {Settings.userInfo.name}
                        {Settings.userInfo.location.length > 0 && <br />}
                        {Settings.userInfo.location}
                    </T.Label>
                </div>
                <form>
                    {/* Application Dropdown */}
                    {Settings.ui.applicationOptions?.length > 1 && (
                        <FormField htmlForm="bba-application-dropdown" label="Select Application">
                            <Dropdown
                                id="bba-application-dropdown"
                                name="application"
                                defaultValue={Settings.ui.defaultApplication}
                                onItemSelect={(e) => {
                                    setApplication(e.value);
                                    setApplicationDisplay(e.label);
                                }}
                                options={Settings.ui.applicationOptions}
                                optionRenderer={(option) => (
                                    <div>
                                        {option.labelDropdown ? option.labelDropdown : option.label}
                                    </div>
                                )}
                            />
                        </FormField>
                    )}

                    <div className="row">
                        {/* Excel Checkbox */}
                        <Checkbox
                            label={
                                <span>
                                    Launch with the <strong>Bloomberg Excel add-in</strong>
                                </span>
                            }
                            defaultChecked={excelChecked}
                            onClick={(event) => setExcelChecked(event.target.checked)}
                        />

                        {/* Excel Tooltip */}
                        <Flyout
                            content={
                                <div className="excelTooltip">
                                    With the Bloomberg Excel Add-In, you can use a remote version of
                                    Microsoft Excel that interacts with Bloomberg API data.
                                    <a
                                        href={Helpers.getBasePath() + "help#section_excel"}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Button onClick={() => excelTooltip.hide()}>
                                            Learn More
                                        </Button>
                                    </a>
                                </div>
                            }
                            onCreate={setExcelTooltip}
                        >
                            <InfoCircle className="info-icon" />
                        </Flyout>
                    </div>

                    {/* Launch Button */}
                    <SplitButton
                        buttonProps={{
                            ref: launchButtonRef,
                            children: "Launch",
                            onClick: () => handleLaunchWithTracking(activeProtocol),
                        }}
                        kind="primary"
                        menuItems={buildLaunchButtonDropdownMenu()}
                    />
                </form>
                <div className="bottom">
                    {/* Unrecognised phases name Parallels, matching the backend. */}
                    <T.BodyLarge as="p" className="link-wrapper">
                        {Settings.migration.phase === MigrationPhases.None
                            ? "Need to install Citrix?"
                            : "Need to install Parallels?"}
                        &nbsp;
                        <a
                            href={Helpers.getBasePath() + "downloads"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Learn More
                        </a>
                    </T.BodyLarge>
                    <div className="region">
                        <div className="row">
                            <Flyout
                                content={
                                    <div className="countryTooltip">
                                        Select your region to help us give you the best possible
                                        experience.
                                    </div>
                                }
                            >
                                <InfoCircle className="info-icon" />
                            </Flyout>
                            <span>
                                {/* as="strong": T.BodyLargeBold defaults to <p>, invalid inside this <span>. */}
                                <T.BodyLargeBold as="strong">Current Region:</T.BodyLargeBold>
                                <Menu
                                    className="bbui-text-dropdown"
                                    kind="tertiary"
                                    compact
                                    triggerText={currentRegion}
                                    /* icon={null} suppresses Menu's default leading hamburger;
                                    hideIcon is left unset so the trailing caret shows. */
                                    icon={null}
                                    menuItems={Settings.ui.regions.map((str) => {
                                        return {
                                            label: str,
                                            onSelect: () => {
                                                setCurrentRegion(str);
                                            },
                                        };
                                    })}
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Unlike MigrationBanner, this survives into the settled phase. */}
            {hasMigrated(Settings.migration.phase) && <MigrationBar />}

            <div
                id="page_progress"
                style={{ display: progressEnabled ? "flex" : "none" }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="launch-progress-label"
            >
                <div className="progress-content">
                    <div className="progress-main">
                        <div className="progress-heading">
                            <ProgressSpinner
                                className="launch-progress-spinner"
                                size="xlarge"
                                label=""
                                aria-label={progressText}
                            />
                            <T.H4 as="div" className="progress-label" id="launch-progress-label">
                                {progressText}
                            </T.H4>
                        </div>
                        <div className="progress-info">
                            {/*Show only if Parallels Client Launch + Session is available (triggered by text change)*/}
                            {currentLaunchProtocol === Constants.Protocols.ParallelsClient &&
                                progressText === Constants.ProgressText.ParallelsClient && (
                                    <>
                                        <T.BodyLarge as="p">
                                            Parallels Client is required to launch BBA, please
                                            download the client
                                            {Settings.ui.availableProtocols.includes(
                                                Constants.Protocols.ParallelsBrowser,
                                            ) && ", or Launch With The Browser"}
                                        </T.BodyLarge>
                                        {Settings.ui.availableProtocols.includes(
                                            Constants.Protocols.ParallelsBrowser,
                                        ) && (
                                            <>
                                                <T.BodyLarge as="p">Having Trouble?</T.BodyLarge>
                                                <div className="progress-buttons">
                                                    <Button
                                                        onClick={handleBrowserLaunchFromProgress}
                                                    >
                                                        Launch With The Browser
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                        </div>
                    </div>
                    <div className="progress-footer">
                        <T.BodyLargeBold
                            as="a"
                            href={Helpers.getBasePath() + "help"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Need additional help?
                        </T.BodyLargeBold>
                    </div>
                </div>
            </div>

            <UpgradeClientModal
                isOpen={isUpgradeModalOpen}
                onClose={closeUpgradeModal}
                installedVersion={Settings.clientVersion.installedVersion}
                latestVersion={Settings.clientVersion.latestVersion}
            />
        </div>
    );
};

export default Launch;
